import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import api from '../api/client'
import { informesEventoApi } from '../api/informesEvento'
import Boton from '../components/Boton'
import BuscadorCliente from '../components/BuscadorCliente'
import BuscadorArticuloGrande from '../components/BuscadorArticuloGrande'
import ModalListaClientes from '../components/ModalListaClientes'
import EstadoBadgeInforme from '../components/EstadoBadgeInforme'
import SelectorArticulosModal from '../components/SelectorArticulosModal'
import PasteExcelModal from '../components/PasteExcelModal'
import { fmtFecha, fmtFechaHora, fmtMonto, parseMonto } from '../utils/fecha'
import { colors, text, space, radius, sx } from '../design/tokens'

/**
 * Informe de evento (Casa VG) — el circuito de Martin (22 sep 2026):
 *
 *   1 · FACTURA        cada lugar se factura con el artículo EVT (fuera de acá).
 *   2 · INFORME        las facturas EVT llegan solas; vinos y gastos a mano;
 *                      "Cerrar evento" congela el balance
 *                      (facturación − (vinos + gastos)).
 *   3 · COMPENSACIÓN   LGR propone la NV al cliente CONSUMOS CASA VG (lugares
 *                      en negativo + vinos + ajuste); se envía a Presea como
 *                      cualquier NV y al facturarse el evento queda COMPLETO.
 *
 * La cuenta NO vive acá: la vista previa la hace el backend
 * (POST /informes-evento/{id}/calcular → InformeEventoService), la misma que
 * congela el cierre y usan los exportes.
 */

// Los vinos se cotizan con LISTA1 al cargarlos; el precio queda fijo y editable.
const LISTA_VINOS = 'LISTA1'

const TIPOS_SUGERIDOS = ['CENA VG', 'CENA PRIVADA', 'DEGUSTACIÓN', 'EVENTO CORPORATIVO', 'POP UP', 'VIAJE']

const num = (v) => { const n = parseMonto(v); return isNaN(n) ? null : n }
const aTexto = (v) => (v === null || v === undefined ? '' : String(v).replace('.', ','))
let uid = 1

const deInforme = (d) => ({
  cab: {
    nombre: d.nombre || '', empresa_contratante: d.empresa_contratante || '', fecha: d.fecha || '',
    tipo_evento: d.tipo_evento || '', comensales: d.comensales ?? '', observaciones: d.observaciones || '',
  },
  gastos: d.gastos.map(g => ({ key: uid++, concepto: g.concepto, monto: aTexto(g.monto) })),
  vinos: d.vinos.map(v => ({ ...v, key: uid++, cantidad: aTexto(v.cantidad), precio_unitario: aTexto(v.precio_unitario) })),
})

export default function InformeEventoFormPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [informe, setInforme] = useState(null)
  const [cab, setCab] = useState(null)
  const [vinos, setVinos] = useState([])
  const [gastos, setGastos] = useState([])
  const [buscarCliente, setBuscarCliente] = useState(false)
  const [nroFactura, setNroFactura] = useState('')
  const buscadorRef = useRef(null)
  const [cliente, setCliente] = useState(null)
  const [calc, setCalc] = useState(null)
  const [precios, setPrecios] = useState({})
  const [selector, setSelector] = useState(false)
  const [ocupado, setOcupado] = useState('')      // qué acción está corriendo
  const [error, setError] = useState('')
  const [mensaje, setMensaje] = useState('')
  const [sucio, setSucio] = useState(false)
  const [pegado, setPegado] = useState('')           // texto pegado para importar vinos
  const [conceptoPropio, setConceptoPropio] = useState('')

  const aplicar = useCallback((data) => {
    setInforme(data)
    const { cab: c, vinos: v, gastos: g } = deInforme(data)
    setCab(c); setVinos(v); setGastos(g); setCliente(data.cliente); setCalc(data.calculos); setSucio(false)
  }, [])

  useEffect(() => {
    informesEventoApi.obtener(id).then(({ data }) => aplicar(data)).catch(() => setError('No se pudo cargar el informe.'))
  }, [id, aplicar])

  useEffect(() => {
    api.get(`/listas-precios/codigo/${LISTA_VINOS}/items`).then(({ data }) => {
      const mapa = {}
      ;(Array.isArray(data) ? data : (data.data || [])).forEach(it => { mapa[it.articulo_id] = parseFloat(it.precio) })
      setPrecios(mapa)
    }).catch(() => setPrecios({}))
  }, [])

  const editable = !!informe?.editable

  // Ctrl+V con tabulaciones fuera de un input → importar vinos (igual que en la nota de venta)
  useEffect(() => {
    if (!editable) return
    const h = (e) => {
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName)) return
      const texto = e.clipboardData?.getData('text') || ''
      if (texto.includes('\t')) { e.preventDefault(); setPegado(texto) }
    }
    document.addEventListener('paste', h)
    return () => document.removeEventListener('paste', h)
  }, [editable])

  // F6 — búsqueda avanzada de cliente (igual que en la nota de venta)
  useEffect(() => {
    if (!editable) return
    const h = (e) => { if (e.key === 'F6') { e.preventDefault(); setBuscarCliente(true) } }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [editable])

  const payload = () => ({
    nombre: cab.nombre.trim(),
    empresa_contratante: cab.empresa_contratante.trim() || null,
    fecha: cab.fecha,
    tipo_evento: cab.tipo_evento.trim() || null,
    comensales: cab.comensales === '' ? null : parseInt(cab.comensales, 10),
    cliente_id: cliente?.id || null,
    observaciones: cab.observaciones.trim() || null,
    gastos: gastos.map(g => ({ concepto: g.concepto.trim(), monto: num(g.monto) })),
    vinos: vinos.map(v => ({
      articulo_id: v.articulo_id || null,
      descripcion: (v.descripcion || '').trim(),
      cantidad: num(v.cantidad),
      precio_unitario: num(v.precio_unitario),
    })),
  })

  // Vista previa en vivo mientras está abierto (debounce) — la cuenta la hace el backend
  const timer = useRef(null)
  useEffect(() => {
    if (!informe || !editable || !sucio) return
    clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      informesEventoApi.calcular(informe.id, payload()).then(({ data }) => setCalc(data)).catch(() => {})
    }, 250)
    return () => clearTimeout(timer.current)
  }, [cab, vinos, gastos, sucio]) // eslint-disable-line react-hooks/exhaustive-deps

  const setCampo = (k, v) => { setCab(c => ({ ...c, [k]: v })); setSucio(true) }
  const setVino = (key, k, v) => { setVinos(vs => vs.map(x => x.key === key ? { ...x, [k]: v } : x)); setSucio(true) }
  const quitarVino = (key) => { setVinos(vs => vs.filter(x => x.key !== key)); setSucio(true) }
  const moverVino = (i, d) => {
    setVinos(vs => { const a = [...vs]; const j = i + d; if (j < 0 || j >= a.length) return a; [a[i], a[j]] = [a[j], a[i]]; return a })
    setSucio(true)
  }
  const agregarDelSelector = (renglones) => {
    setVinos(vs => {
      const nuevos = [...vs]
      renglones.forEach(({ articulo, cantidad }) => {
        const k = nuevos.findIndex(x => x.articulo_id === articulo.id)
        if (k >= 0) { nuevos[k] = { ...nuevos[k], cantidad: aTexto((num(nuevos[k].cantidad) || 0) + cantidad) }; return }
        const precio = precios[articulo.id]
        nuevos.push({
          key: uid++, articulo_id: articulo.id, codigo: articulo.codigo_alfa,
          descripcion: articulo.descripcion, cantidad: aTexto(cantidad), precio_unitario: precio ? aTexto(precio) : '',
        })
      })
      return nuevos
    })
    setSelector(false); setSucio(true)
    setTimeout(() => buscadorRef.current?.focus(), 0)
  }
  const agregarUno = (articulo) => agregarDelSelector([{ articulo, cantidad: 1 }])
  // Importar: las filas pegadas (código · detalle · cantidad · precio), el mismo modal que la NV.
  // El precio pegado manda; si no vino, el de LISTA1.
  const agregarPegados = (items) => {
    setVinos(vs => {
      const nuevos = [...vs]
      items.forEach(({ articulo, cantidad, precio_lista }) => {
        const k = nuevos.findIndex(x => x.articulo_id === articulo.id)
        if (k >= 0) { nuevos[k] = { ...nuevos[k], cantidad: aTexto((num(nuevos[k].cantidad) || 0) + cantidad) }; return }
        nuevos.push({
          key: uid++, articulo_id: articulo.id, codigo: articulo.codigo_alfa, descripcion: articulo.descripcion,
          cantidad: aTexto(cantidad), precio_unitario: precio_lista ? aTexto(precio_lista) : '',
        })
      })
      return nuevos
    })
    setSucio(true)
  }
  const importarPegando = async () => {
    try {
      const texto = await navigator.clipboard.readText()
      if (texto?.trim()) setPegado(texto)
      else setError('El portapapeles está vacío: copiá las filas (código, detalle, cantidad, precio) desde la planilla.')
    } catch {
      setError('No se pudo leer el portapapeles. Hacé clic fuera de los campos de texto y presioná Ctrl+V.')
    }
  }
  const setGasto = (key, k, v) => { setGastos(gs => gs.map(x => x.key === key ? { ...x, [k]: v } : x)); setSucio(true) }
  const quitarGasto = (key) => { setGastos(gs => gs.filter(x => x.key !== key)); setSucio(true) }
  const agregarGasto = (concepto = '') => { setGastos(gs => [...gs, { key: uid++, concepto, monto: '' }]); setSucio(true) }
  const agregarLibre = () => {
    setVinos(vs => [...vs, { key: uid++, articulo_id: null, codigo: null, descripcion: '', cantidad: '', precio_unitario: '' }])
    setSucio(true)
  }

  // Corre una acción del circuito y muestra el resultado
  const accion = async (nombre, fn, ok) => {
    setError(''); setMensaje(''); setOcupado(nombre)
    try {
      const { data } = await fn()
      if (data) aplicar(data)
      if (ok) setMensaje(ok)
      return true
    } catch (err) {
      const errs = err.response?.data?.errors
      setError(errs ? Object.values(errs).flat().join(' ') : (err.response?.data?.message || 'No se pudo completar la acción.'))
      return false
    } finally {
      setOcupado('')
    }
  }

  const guardar = () => {
    if (!cab.nombre.trim()) { setError('Falta el nombre del evento.'); return }
    if (!cab.fecha) { setError('Falta la fecha del evento.'); return }
    if (vinos.some(v => !(v.descripcion || '').trim() || !num(v.cantidad) || num(v.precio_unitario) === null)) {
      setError('Hay un vino sin nombre, cantidad o precio.'); return
    }
    if (gastos.some(g => !g.concepto.trim() || num(g.monto) === null)) {
      setError('Hay un gasto sin concepto o sin monto.'); return
    }
    return accion('guardar', () => informesEventoApi.actualizar(informe.id, payload()), 'Informe guardado.')
  }
  const sincronizar = () => accion('sincronizar', () => informesEventoApi.sincronizar(informe.id), 'Facturas EVT actualizadas.')
  const traerFactura = async () => {
    if (sucio) { setError('Guardá los cambios antes de tocar las facturas.'); return }
    const ok = await accion('factura', () => informesEventoApi.agregarFactura(informe.id, nroFactura.trim()), 'Comprobante agregado.')
    if (ok) setNroFactura('')
  }
  const quitarFactura = (f) => {
    if (sucio) { setError('Guardá los cambios antes de tocar las facturas.'); return }
    accion('factura', () => informesEventoApi.quitarFactura(informe.id, f.fila_id), f.origen === 'manual' ? 'Factura quitada.' : 'Factura sacada: no suma y no vuelve sola al actualizar.')
  }
  const incluirFactura = (f) => {
    if (sucio) { setError('Guardá los cambios antes de tocar las facturas.'); return }
    accion('factura', () => informesEventoApi.incluirFactura(informe.id, f.fila_id), 'Factura incluida de nuevo.')
  }
  const cerrar = async () => {
    if (sucio) { setError('Guardá los cambios antes de cerrar el evento.'); return }
    if (!window.confirm(`¿Cerrar el evento con balance $ ${fmtMonto(calc?.balance || 0)}? Queda congelado y ya no se edita.`)) return
    accion('cerrar', () => informesEventoApi.cerrar(informe.id), 'Evento cerrado: el balance quedó congelado.')
  }
  const reabrir = () => {
    if (!window.confirm('¿Reabrir el evento? El balance vuelve a calcularse con lo que haya.')) return
    accion('reabrir', () => informesEventoApi.reabrir(informe.id), 'Evento reabierto.')
  }
  const generarCompensacion = () => accion('compensacion', () => informesEventoApi.compensacion(informe.id),
    'NV de compensación generada en borrador: revisala y enviala a Presea.')
  const exportar = async (formato) => {
    setError('')
    try { await informesEventoApi.exportar(informe, formato) } catch { setError(`No se pudo exportar a ${formato.toUpperCase()}.`) }
  }
  const eliminar = async () => {
    if (!window.confirm(`¿Eliminar el informe ${informe.numero}? No se puede deshacer.`)) return
    try { await informesEventoApi.eliminar(informe.id); navigate('/eventos/informes') } catch (err) { setError(err.response?.data?.message || 'No se pudo eliminar.') }
  }

  if (!informe || !cab) {
    return <div style={{ padding: 24, color: error ? colors.err : colors.textMeta }}>{error || 'Cargando…'}</div>
  }

  const etapa = informe.estado === 'abierto' ? 2 : informe.estado === 'cerrado' ? 3 : 4

  return (
    <div style={{ maxWidth: 1200 }}>
      {/* Toolbar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap', marginBottom: space.lg }}>
        <div>
          <Boton variante="ghost" sm style={{ marginBottom: 4 }} onClick={() => navigate(-1)}>← Volver</Boton>
          <div style={{ fontSize: text.meta, fontWeight: 600, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Informe de evento</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: space.md }}>
            <h1 style={{ margin: 0, ...sx.mono }}>{informe.numero}</h1>
            <EstadoBadgeInforme estado={informe.estado} />
          </div>
          <div style={{ fontSize: text.sec, color: colors.textMeta }}>
            Creado por {informe.creado_por || '—'}
            {informe.cerrado_at && <> · cerrado {fmtFechaHora(informe.cerrado_at)} por {informe.cerrado_por || '—'}</>}
            {informe.completado_at && <> · completo {fmtFechaHora(informe.completado_at)} por {informe.completado_por || '—'}</>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Boton variante="secondary" disabled={sucio} title={sucio ? 'Guardá los cambios antes de exportar' : 'Planilla con las fórmulas'} onClick={() => exportar('xlsx')}>⬇ Excel</Boton>
          <Boton variante="secondary" disabled={sucio} title={sucio ? 'Guardá los cambios antes de exportar' : 'Texto plano'} onClick={() => exportar('txt')}>⬇ TXT</Boton>
          <Boton variante="secondary" disabled={sucio} title={sucio ? 'Guardá los cambios antes de exportar' : 'PDF'} onClick={() => exportar('pdf')}>⬇ PDF</Boton>
          {informe.estado === 'cerrado' && <Boton variante="ghost" disabled={!!ocupado} onClick={reabrir}>↺ Reabrir</Boton>}
          {informe.estado !== 'completo' && <Boton variante="destructive" onClick={eliminar}>Eliminar</Boton>}
          {editable && <Boton variante="primary" disabled={!!ocupado || !sucio} onClick={guardar}>{ocupado === 'guardar' ? 'Guardando…' : 'Guardar'}</Boton>}
        </div>
      </div>

      <Etapas etapa={etapa} />

      {mensaje && <div style={{ ...caja, background: colors.okBg, color: colors.ok }}>{mensaje}</div>}
      {error && <div style={{ ...caja, background: colors.errBg, color: colors.err }}>{error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 320px', gap: space.xl, alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: space.xl, minWidth: 0 }}>

          {/* Datos del evento */}
          <div style={sx.card}>
            <h3 style={sx.cardTitle}>Datos del evento</h3>
            <div style={grilla}>
              <Campo label="Nombre del evento *" ancho={2}>
                <input style={inp} disabled={!editable} value={cab.nombre} onChange={e => setCampo('nombre', e.target.value)} />
              </Campo>
              <Campo label="Evento (artículo EVT)" ancho={2}>
                <div style={{ ...inp, background: colors.bgSoft, color: colors.textSub, ...sx.mono, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={informe.articulos.map(a => `${a.codigo_alfa} ${a.descripcion}`).join('\n')}>
                  {informe.articulos.map(a => a.codigo_alfa).join(' · ')}
                </div>
              </Campo>
              <Campo label="Fecha *">
                <input type="date" style={inp} disabled={!editable} value={cab.fecha} onChange={e => setCampo('fecha', e.target.value)} />
              </Campo>
              <Campo label="Tipo de evento">
                <input style={inp} disabled={!editable} list="tipos-evento" value={cab.tipo_evento} onChange={e => setCampo('tipo_evento', e.target.value)} />
                <datalist id="tipos-evento">{TIPOS_SUGERIDOS.map(t => <option key={t} value={t} />)}</datalist>
              </Campo>
              <Campo label="Comensales (asistentes)">
                <input style={{ ...inp, ...sx.mono, textAlign: 'right' }} disabled={!editable} inputMode="numeric" value={cab.comensales} onChange={e => setCampo('comensales', e.target.value.replace(/\D/g, ''))} />
              </Campo>
              <Campo label="Lugares facturados">
                <div style={{ ...inp, background: colors.bgSoft, ...sx.mono, textAlign: 'right' }}>{fmtMonto(calc?.lugares || 0, 0)}</div>
              </Campo>
              <Campo label="Empresa contratante" ayuda="Quién encarga el evento (texto libre). Sale en el informe." ancho={2}>
                <input style={inp} disabled={!editable} value={cab.empresa_contratante} placeholder="Ej. INNOVATEMPO S.A." onChange={e => setCampo('empresa_contratante', e.target.value)} />
              </Campo>
              <Campo label="Cliente en LGR" ayuda="La ficha de cliente del evento privado (la que se factura). Opcional. F6 = búsqueda avanzada." ancho={2}>
                {editable ? (
                  <div style={{ display: 'flex', gap: space.sm, alignItems: 'flex-start' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <BuscadorCliente key={cliente?.id || 'sin'} value={cliente} onChange={c => { setCliente(c); setSucio(true) }} />
                    </div>
                    <Boton variante="secondary" sm type="button" onClick={() => setBuscarCliente(true)} title="Búsqueda avanzada de cliente (F6): nombre, CUIT, DNI, localidad, email">🔍</Boton>
                  </div>
                ) : <div style={{ ...inp, background: colors.bgSoft }}>{cliente ? cliente.nombre : '—'}</div>}
                {cliente && (
                  <div style={{ fontSize: text.sec, color: colors.textSec, marginTop: 4 }}>
                    Nº cliente <span style={sx.mono}>{cliente.codigo_presea || 'pendiente'}</span>
                    {editable && <>{' · '}<button type="button" style={linkBtn} onClick={() => { setCliente(null); setSucio(true) }}>quitar</button></>}
                  </div>
                )}
              </Campo>
            </div>
          </div>

          {/* 2.2 — Facturas EVT (automáticas + manuales) */}
          <div id="etapa-facturas" style={sx.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: space.lg, gap: space.md, flexWrap: 'wrap' }}>
              <div>
                <h3 style={sx.cardTitle}>Comprobantes del evento</h3>
                <div style={{ fontSize: text.sec, color: colors.textMeta, marginTop: 2 }}>
                  Llegan solos todos los del artículo del evento, estén donde estén: LGR, Presea y la 900. Las notas de crédito y devoluciones restan del total.
                </div>
                {(informe.compensaciones_anuladas || []).map((a, i) => (
                  <div key={i} style={{ fontSize: text.sec, color: colors.warn, marginTop: 4 }}>
                    Compensación anulada: factura <span style={sx.mono}>{a.factura}</span>
                    {a.notas_credito?.length > 0 && <> con <span style={sx.mono}>{a.notas_credito.join(', ')}</span></>}
                    {' '}({fmtFecha(a.fecha)}) — no cuentan como venta del evento.
                  </div>
                ))}
              </div>
              {editable && (
                <div style={{ display: 'flex', gap: space.sm, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    style={{ ...sx.input, ...sx.mono, width: 190 }}
                    value={nroFactura}
                    placeholder="Nº de comprobante o LGR-…"
                    onChange={e => setNroFactura(e.target.value.trim())}
                    onKeyDown={e => { if (e.key === 'Enter' && nroFactura) traerFactura() }}
                  />
                  <Boton variante="secondary" sm disabled={!nroFactura || !!ocupado} onClick={traerFactura}>+ Traer comprobante</Boton>
                  <Boton variante="ghost" sm disabled={!!ocupado} onClick={sincronizar} title="Vuelve a buscar las facturas del evento (nuevas o anuladas)">{ocupado === 'sincronizar' ? 'Actualizando…' : '↻ Actualizar'}</Boton>
                </div>
              )}
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ ...tablaNv, minWidth: 860 }}>
                <colgroup>
                  <col style={{ width: 168 }} /><col style={{ minWidth: 180 }} /><col style={{ width: 70 }} /><col style={{ width: 126 }} /><col style={{ width: 126 }} /><col style={{ width: 126 }} />
                  {editable && <col style={{ width: 84 }} />}
                </colgroup>
                <thead>
                  <tr>
                    <th style={thNv}>Comprobante</th><th style={thNv}>Cliente · fecha</th>
                    <th style={{ ...thNv, textAlign: 'right' }}>Lugares</th>
                    <th style={{ ...thNv, textAlign: 'right' }}>Evento c/IVA</th>
                    <th style={{ ...thNv, textAlign: 'right' }}>Evento s/IVA</th>
                    <th style={{ ...thNv, textAlign: 'right' }} title="Total del comprobante informado por Presea (incluye percepciones y los otros renglones). Solo para los que salieron de LGR.">Total cbte</th>
                    {editable && <th style={thNv}></th>}
                  </tr>
                </thead>
                <tbody>
                  {informe.facturas.length === 0 && (
                    <tr><td colSpan={7} style={{ padding: 16, textAlign: 'center', color: colors.textMeta }}>No hay comprobantes con el artículo del evento: traé uno con su número.</td></tr>
                  )}
                  {informe.facturas.map(f => {
                    const fuera = f.excluida
                    const tachado = fuera ? { textDecoration: 'line-through', color: colors.textMeta } : {}
                    return (
                      <tr key={f.fila_id} style={{ borderBottom: `1px solid ${colors.borderSoft}`, ...(fuera ? { background: colors.bgSoft } : {}) }}>
                        <td style={celda}>
                          <div style={{ ...sx.mono, whiteSpace: 'nowrap', ...tachado }}>
                            {f.nota_venta_id
                              ? <Link to={`/notas-venta/${f.nota_venta_id}`} style={{ color: fuera ? colors.textMeta : colors.link }} title={`Ver ${f.numero_lgr || 'la nota de venta'}`}>{f.numero}</Link>
                              : <span title="Hecho en Presea (no tiene nota de venta en LGR)">{f.numero}</span>}
                          </div>
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 2 }}>
                            <span style={{ fontSize: text.meta, color: colors.textMeta }}>{f.tipo_cbte}</span>
                            {f.grupo === 'EMP_9' && <span style={{ ...sx.badge, background: colors.okBg, color: colors.ok }} title="Comprobante de la EMP 900">900</span>}
                            {f.es_nc && <span style={{ ...sx.badge, background: colors.errBg, color: colors.err }} title="Nota de crédito / devolución: resta del total">NC</span>}
                            {f.origen === 'manual' && <span style={{ ...sx.badge, background: colors.t80Bg, color: colors.t80Text }} title="Lo trajiste a mano">manual</span>}
                            {fuera && <span style={{ ...sx.badge, background: colors.borderSoft, color: colors.textSub }} title={f.es_nc ? 'La sacaste: no resta y no vuelve sola' : 'Lo sacaste: no suma y no vuelve solo'}>fuera</span>}
                          </div>
                        </td>
                        <td style={{ ...celda, ...tachado }}>
                          <div style={{ lineHeight: 1.3 }}>{f.cliente || '—'}</div>
                          <div style={{ ...sx.mono, fontSize: text.sec, color: colors.textMeta }}>{f.codigo_cliente ? `${f.codigo_cliente} · ` : ''}{fmtFecha(f.fecha)}</div>
                        </td>
                        <td style={{ ...celda, ...sx.mono, textAlign: 'right', ...tachado }}>{fmtMonto(f.lugares, 0)}</td>
                        <td style={{ ...celda, ...sx.mono, textAlign: 'right', whiteSpace: 'nowrap', ...tachado }}>$ {fmtMonto(f.evento_con_iva)}</td>
                        <td style={{ ...celda, ...sx.mono, textAlign: 'right', whiteSpace: 'nowrap', ...tachado }}>
                          {f.evento_sin_iva === null ? <span style={{ ...sx.badge, background: colors.warnBg, color: colors.warn }}>sin alícuota</span> : `$ ${fmtMonto(f.evento_sin_iva)}`}
                        </td>
                        <td style={{ ...celda, ...sx.mono, textAlign: 'right', whiteSpace: 'nowrap', color: colors.textSub, ...tachado }}>
                          {f.presea_importe === null ? '—' : `$ ${fmtMonto(f.presea_importe)}`}
                        </td>
                        {editable && (
                          <td style={{ ...celda, textAlign: 'right', whiteSpace: 'nowrap' }}>
                            {fuera
                              ? <Boton variante="ghost" sm disabled={!!ocupado} onClick={() => incluirFactura(f)} title={f.es_nc ? 'Incluir la nota de crédito: resta de la facturación' : 'Volver a sumarlo'}>↩ Incluir</Boton>
                              : <Boton variante="destructive" sm disabled={!!ocupado} onClick={() => quitarFactura(f)} title={f.origen === 'manual' ? 'Quitar del informe' : f.es_nc ? 'Sacarla: deja de restar y no vuelve sola' : 'Sacarlo: no suma y no vuelve solo'}>✕</Boton>}
                          </td>
                        )}
                      </tr>
                    )
                  })}
                </tbody>
                {informe.facturas.length > 0 && (
                  <tfoot>
                    <tr>
                      <td colSpan={2} style={{ ...celda, textAlign: 'right', fontWeight: 600, color: colors.textSec }}>Facturación ({calc?.facturas} comprobantes)</td>
                      <td style={{ ...celda, ...sx.mono, textAlign: 'right', fontWeight: 600 }}>{fmtMonto(calc?.lugares || 0, 0)}</td>
                      <td style={{ ...celda, ...sx.mono, textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap' }}>$ {fmtMonto(calc?.facturacion_con_iva || 0)}</td>
                      <td style={{ ...celda, ...sx.mono, textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' }}>$ {fmtMonto(calc?.facturacion || 0)}</td>
                      <td colSpan={editable ? 2 : 1} />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>

          {/* 2.3 — Vinos consumidos (manual) — misma carga que la nota de venta */}
          <div style={sx.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: space.md, gap: space.md, flexWrap: 'wrap' }}>
              <h3 style={sx.cardTitle}>Vinos consumidos <span style={{ textTransform: 'none', fontWeight: 400, color: colors.textMeta }}>· a precio de lista (LISTA1)</span></h3>
              {editable && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <Boton variante="secondary" sm onClick={() => setSelector(true)} title="Buscar y agregar varios artículos">Buscar artículos</Boton>
                  <Boton variante="secondary" sm onClick={importarPegando} title="Pegar filas copiadas de una planilla: código · detalle · cantidad · precio (también con Ctrl+V)">📋 Importar productos</Boton>
                  <Boton variante="secondary" sm onClick={agregarLibre} title="Un vino que no está en el maestro">+ Renglón libre</Boton>
                  {vinos.length > 0 && <Boton variante="destructive" sm onClick={() => { if (window.confirm('¿Vaciar la lista de vinos?')) { setVinos([]); setSucio(true) } }}>🗑 Vaciar</Boton>}
                </div>
              )}
            </div>
            {editable && (
              <BuscadorArticuloGrande
                inputRef={buscadorRef}
                onSelect={agregarUno}
                precios={precios}
                lista={LISTA_VINOS}
                placeholder="Buscar vino por descripción, código, bodega, cepa, año…  (↑↓ elige, Enter agrega)"
              />
            )}
            {vinos.length === 0 ? (
              <p style={{ color: colors.textMeta, textAlign: 'center', padding: `${space.xl}px 0`, margin: 0, fontSize: text.body }}>
                {editable ? 'Buscá los vinos arriba, usá "Buscar artículos" o "Importar productos" (Ctrl+V desde la planilla).' : 'Sin vinos cargados.'}
              </p>
            ) : (
              <div style={{ overflowX: 'auto', marginTop: space.md }}>
                <table style={tablaNv}>
                  <colgroup>
                    <col style={{ width: 96 }} /><col /><col style={{ width: 64 }} /><col style={{ width: 110 }} /><col style={{ width: 136 }} />
                    {editable && <col style={{ width: 96 }} />}
                  </colgroup>
                  <thead>
                    <tr>
                      <th style={thNv}>Código</th>
                      <th style={thNv}>Descripción</th>
                      <th style={{ ...thNv, textAlign: 'right' }}>Cant.</th>
                      <th style={{ ...thNv, textAlign: 'right' }}>Precio</th>
                      <th style={{ ...thNv, textAlign: 'right' }}>Subtotal</th>
                      {editable && <th style={thNv}></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {vinos.map((v, i) => {
                      const sinPrecio = num(v.precio_unitario) === null
                      return (
                        <tr key={v.key} style={{ height: 44, borderBottom: `1px solid ${colors.borderSoft}`, ...(sinPrecio ? { background: colors.attnBg } : {}) }}>
                          <td style={tdNv}><code style={{ ...sx.mono, fontSize: text.sec }}>{v.codigo || '—'}</code></td>
                          <td style={{ ...tdNv, overflow: 'hidden' }}>
                            {v.articulo_id || !editable
                              ? <span style={{ lineHeight: 1.3 }}>{v.descripcion}</span>
                              : <input className="celda-grilla" style={{ ...celdaNv, fontFamily: 'inherit', textAlign: 'left' }} value={v.descripcion} placeholder="Nombre del vino" onChange={e => setVino(v.key, 'descripcion', e.target.value)} />}
                            {sinPrecio && editable && <div style={{ marginTop: 2, fontSize: text.meta, color: colors.warn, fontWeight: 600 }}>⚠ Sin precio en lista — ingresá manual.</div>}
                          </td>
                          <td style={tdNv}>
                            {editable
                              ? <input className="celda-grilla" style={celdaNv} inputMode="decimal" value={v.cantidad} onFocus={e => e.target.select()} onChange={e => setVino(v.key, 'cantidad', e.target.value)} />
                              : <div style={{ ...sx.mono, textAlign: 'right' }}>{v.cantidad}</div>}
                          </td>
                          <td style={tdNv}>
                            {editable
                              ? <input className="celda-grilla" style={celdaNv} inputMode="decimal" value={v.precio_unitario} onFocus={e => e.target.select()} onChange={e => setVino(v.key, 'precio_unitario', e.target.value)} />
                              : <div style={{ ...sx.mono, textAlign: 'right' }}>$ {fmtMonto(num(v.precio_unitario))}</div>}
                          </td>
                          <td style={{ ...tdNv, ...sx.mono, textAlign: 'right', whiteSpace: 'nowrap' }}>{calc?.vinos_totales?.[i] != null ? `$ ${fmtMonto(calc.vinos_totales[i])}` : '—'}</td>
                          {editable && (
                            <td style={{ ...tdNv, textAlign: 'right', whiteSpace: 'nowrap' }}>
                              <Boton variante="ghost" sm disabled={i === 0} onClick={() => moverVino(i, -1)} title="Subir">↑</Boton>
                              <Boton variante="ghost" sm disabled={i === vinos.length - 1} onClick={() => moverVino(i, 1)} title="Bajar">↓</Boton>
                              <Boton variante="destructive" sm onClick={() => quitarVino(v.key)} title="Quitar renglón">✕</Boton>
                            </td>
                          )}
                        </tr>
                      )
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={4} style={{ ...tdNv, textAlign: 'right', fontWeight: 600, color: colors.textSec, height: 40 }}>Total vinos (lista, con IVA)</td>
                      <td style={{ ...tdNv, ...sx.mono, textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' }}>$ {fmtMonto(calc?.vinos_con_iva || 0)}</td>
                      {editable && <td />}
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          {/* 2.4 — Gastos del evento: renglones libres */}
          <div style={sx.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: space.md, gap: space.md }}>
              <h3 style={sx.cardTitle}>Gastos del evento</h3>
              {editable && <Boton variante="secondary" sm onClick={() => agregarGasto()}>+ Agregar gasto</Boton>}
            </div>
            {editable && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: space.md }}>
                <span style={{ fontSize: text.sec, color: colors.textSec, marginRight: 2 }}>Agregar rápido:</span>
                {informe.conceptos_gasto.filter(c => !gastos.some(g => g.concepto.trim().toLowerCase() === c.toLowerCase())).map(c => (
                  <Boton key={c} variante="ghost" sm onClick={() => agregarGasto(c)}>+ {c}</Boton>
                ))}
                <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                  <input
                    style={{ ...sx.input, width: 190 }}
                    value={conceptoPropio}
                    placeholder="Concepto propio…"
                    onChange={e => setConceptoPropio(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && conceptoPropio.trim()) { agregarGasto(conceptoPropio.trim()); setConceptoPropio('') } }}
                  />
                  <Boton variante="secondary" sm disabled={!conceptoPropio.trim()} title="Queda en los rápidos para los próximos informes"
                    onClick={() => { agregarGasto(conceptoPropio.trim()); setConceptoPropio('') }}>+ Agregar</Boton>
                </span>
              </div>
            )}
            <datalist id="conceptos-gasto">{informe.conceptos_gasto.map(c => <option key={c} value={c} />)}</datalist>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: text.body }}>
              <colgroup><col /><col style={{ width: 190 }} />{editable && <col style={{ width: 52 }} />}</colgroup>
              <thead>
                <tr>
                  <th style={thNv}>Concepto</th>
                  <th style={{ ...thNv, textAlign: 'right' }}>Monto</th>
                  {editable && <th style={thNv}></th>}
                </tr>
              </thead>
              <tbody>
                <tr style={{ height: 44, borderBottom: `1px solid ${colors.borderSoft}`, background: colors.bgSoft }}>
                  <td style={tdNv}>
                    Vinos consumidos <span style={{ fontSize: text.sec, color: colors.textMeta }}>· automático: total vinos ÷ 1,21</span>
                  </td>
                  <td style={{ ...tdNv, ...sx.mono, textAlign: 'right' }}>$ {fmtMonto(calc?.vinos_sin_iva || 0)}</td>
                  {editable && <td />}
                </tr>
                {gastos.map(g => (
                  <tr key={g.key} style={{ height: 44, borderBottom: `1px solid ${colors.borderSoft}` }}>
                    <td style={tdNv}>
                      {editable
                        ? <input className="celda-grilla" list="conceptos-gasto" style={{ ...celdaNv, fontFamily: 'inherit', textAlign: 'left' }} value={g.concepto} placeholder="Concepto del gasto" onChange={e => setGasto(g.key, 'concepto', e.target.value)} />
                        : g.concepto}
                    </td>
                    <td style={tdNv}>
                      {editable
                        ? <input className="celda-grilla" style={celdaNv} inputMode="decimal" value={g.monto} placeholder="0,00" onFocus={e => e.target.select()} onChange={e => setGasto(g.key, 'monto', e.target.value)} />
                        : <div style={{ ...sx.mono, textAlign: 'right' }}>$ {fmtMonto(num(g.monto) || 0)}</div>}
                    </td>
                    {editable && <td style={{ ...tdNv, textAlign: 'right' }}><Boton variante="destructive" sm onClick={() => quitarGasto(g.key)} title="Quitar gasto">✕</Boton></td>}
                  </tr>
                ))}
                {gastos.length === 0 && (
                  <tr><td colSpan={3} style={{ padding: 14, textAlign: 'center', color: colors.textMeta }}>Sin gastos cargados. Usá "+ Agregar gasto" o los rápidos de arriba.</td></tr>
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td style={{ ...tdNv, fontWeight: 600, height: 40 }}>Total gasto <span style={{ fontWeight: 400, fontSize: text.sec, color: colors.textMeta }}>(vinos + {gastos.length} {gastos.length === 1 ? 'gasto' : 'gastos'})</span></td>
                  <td style={{ ...tdNv, ...sx.mono, textAlign: 'right', fontWeight: 700 }}>$ {fmtMonto(calc?.total_gasto || 0)}</td>
                  {editable && <td />}
                </tr>
              </tfoot>
            </table>
          </div>

          {/* 3 — Factura de compensación: siempre a la vista; se habilita al cerrar */}
          <Compensacion
            informe={informe} calc={calc} vinos={vinos}
            onGenerar={generarCompensacion} generando={ocupado === 'compensacion'}
          />

          <div style={sx.card}>
            <h3 style={{ ...sx.cardTitle, marginBottom: space.md }}>Observaciones</h3>
            <textarea style={{ ...inp, minHeight: 60, resize: 'vertical' }} disabled={!editable} value={cab.observaciones} onChange={e => setCampo('observaciones', e.target.value)} />
          </div>
        </div>

        {/* 2.5 — Balance (fijo a la derecha) */}
        <div style={{ ...sx.card, position: 'sticky', top: 16 }}>
          <h3 style={{ ...sx.cardTitle, marginBottom: space.lg }}>{editable ? 'Balance del evento' : 'Balance final (congelado)'}</h3>
          <Resumen label="Facturación (sin IVA)" monto={calc?.facturacion} />
          <Resumen label="Vinos (sin IVA)" monto={calc?.vinos_sin_iva} menos />
          <Resumen label="Gastos" monto={(calc?.total_gasto || 0) - (calc?.vinos_sin_iva || 0)} menos />
          {/* El importe va en su propio renglón: con 7 cifras no entra al lado del rótulo. */}
          <div style={{ borderTop: `1px solid ${colors.borderStrong}`, marginTop: space.md, paddingTop: space.md }}>
            <div style={{ fontWeight: 600, fontSize: text.sec, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Balance</div>
            <div style={{ ...sx.mono, fontSize: text.display, fontWeight: 700, lineHeight: 1.15, textAlign: 'right', whiteSpace: 'nowrap', color: (calc?.balance || 0) < 0 ? colors.err : colors.text }}>
              $ {fmtMonto(calc?.balance || 0)}
            </div>
          </div>
          {calc?.facturacion > 0 && (
            <div style={{ fontSize: text.sec, color: colors.textMeta, marginTop: space.sm, textAlign: 'right' }}>
              {fmtMonto((calc.balance / calc.facturacion) * 100, 1)} % de la facturación
              {calc.cubierto_sin_iva != null && <> · cubierto $ {fmtMonto(calc.cubierto_sin_iva)} s/IVA</>}
            </div>
          )}
          {calc?.facturas_sin_alicuota > 0 && (
            <div style={{ fontSize: text.sec, color: colors.warn, marginTop: space.md }}>
              {calc.facturas_sin_alicuota} factura(s) con un artículo EVT sin alícuota de IVA en el maestro: no se puede cerrar hasta corregirlo.
            </div>
          )}
          {editable && (
            <div style={{ marginTop: space.xl }}>
              <Boton variante="success" disabled={!!ocupado || sucio || !calc?.facturas} onClick={cerrar} style={{ width: '100%' }}
                title={sucio ? 'Guardá los cambios antes de cerrar' : !calc?.facturas ? 'Sin facturas EVT no hay facturación para cerrar' : 'Congela el balance y pasa a la factura de compensación'}>
                {ocupado === 'cerrar' ? 'Cerrando…' : '✓ Cerrar evento'}
              </Boton>
              {sucio && <div style={{ fontSize: text.sec, color: colors.warn, marginTop: space.sm }}>Hay cambios sin guardar.</div>}
            </div>
          )}
        </div>
      </div>

      {buscarCliente && (
        <ModalListaClientes
          onSelect={(c) => { setCliente(c); setSucio(true); setBuscarCliente(false) }}
          onClose={() => setBuscarCliente(false)}
        />
      )}
      {pegado && (
        <PasteExcelModal
          clipboardText={pegado}
          precios={precios}
          onConfirm={agregarPegados}
          onClose={() => setPegado('')}
        />
      )}
      {selector && (
        <SelectorArticulosModal
          titulo="Vinos consumidos en el evento"
          precios={precios}
          lista={LISTA_VINOS}
          onConfirm={agregarDelSelector}
          onClose={() => setSelector(false)}
        />
      )}
    </div>
  )
}

/** Las 3 etapas del circuito, con la actual resaltada. */
function Etapas({ etapa }) {
  const pasos = [
    ['1', 'Facturas EVT', 'cada lugar facturado', 'etapa-facturas'],
    ['2', 'Informe', 'vinos, gastos y cierre con balance', 'etapa-facturas'],
    ['3', 'Compensación', 'NV a CONSUMOS CASA VG, lista para Presea', 'etapa-compensacion'],
  ]
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: space.md, marginBottom: space.xl }}>
      {pasos.map(([n, titulo, sub, destino], i) => {
        const nro = i + 1
        const hecho = nro < etapa
        const actual = nro === etapa
        return (
          <div key={n} title="Ir a esta etapa" onClick={() => document.getElementById(destino)?.scrollIntoView({ behavior: 'smooth', block: 'start' })} style={{
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: space.md, padding: '8px 12px', borderRadius: radius.md,
            border: `1px solid ${actual ? colors.linkBorder : hecho ? colors.okBg : colors.border}`,
            background: actual ? colors.linkBg : hecho ? colors.okSoft : colors.bg,
          }}>
            <span style={{
              width: 22, height: 22, borderRadius: radius.pill, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              fontSize: text.sec, fontWeight: 700, flexShrink: 0,
              background: hecho ? colors.ok : actual ? colors.primary : colors.borderSoft, color: hecho || actual ? colors.bg : colors.textSec,
            }}>{hecho ? '✓' : n}</span>
            <span style={{ minWidth: 0 }}>
              <div style={{ fontSize: text.body, fontWeight: 600, color: hecho ? colors.ok : colors.text }}>{titulo}</div>
              <div style={{ fontSize: text.sec, color: colors.textMeta }}>{sub}</div>
            </span>
          </div>
        )
      })}
    </div>
  )
}

/**
 * Etapa 3: la NV de compensación (Martin, 23 sep 2026). Los renglones los arma
 * el backend (InformeEventoService::compensacion) — la misma cuenta que usa la
 * NV que se genera; acá solo se pintan. Se genera con el evento cerrado y
 * queda en borrador para enviarla a Presea como cualquier NV.
 */
const ROL_COMP = {
  eventos: () => 'Lugares vendidos (en negativo)',
  vino: (r, vinos) => vinos[r.indice]?.descripcion || 'Vino',
  gastos: (r, _v, tipo) => tipo === 'descuento' ? 'Redondeo del descuento · EVT00099' : 'Gastos eventos · EVT00099',
  ajuste: () => 'Ajuste · AFIP0000',
}

function Compensacion({ informe, calc, vinos, onGenerar, generando }) {
  const navigate = useNavigate()
  const comp = calc?.compensacion
  const abierto = informe.estado === 'abierto'
  const completo = informe.estado === 'completo'
  const nv = informe.compensacion_nv
  const tipo = comp?.ajuste?.tipo
  return (
    <div id="etapa-compensacion" style={{ ...sx.card, borderColor: completo ? colors.okBg : abierto ? colors.border : colors.linkBorder }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: space.lg, gap: space.md }}>
        <h3 style={sx.cardTitle}>3 · Nota de venta de compensación</h3>
        {abierto && <span style={{ fontSize: text.sec, color: colors.textMeta }}>Se genera al cerrar el evento</span>}
      </div>

      <div style={{ display: 'flex', gap: space.md, alignItems: 'center', flexWrap: 'wrap', padding: `${space.md}px ${space.lg}px`, borderRadius: radius.md, background: completo ? colors.okSoft : abierto ? colors.bgSoft : colors.linkBg }}>
        {completo ? (
          <>
            <span style={{ ...sx.badge, background: colors.okBg, color: colors.ok }}>✓ Facturada en Presea</span>
            <span style={sx.mono}>{informe.compensacion_numero}</span>
            <span style={{ color: colors.textSec, fontSize: text.sec }}>{informe.compensacion_formulario || 'FACTURA'} · {fmtFecha(informe.compensacion_fecha)}</span>
            {nv && <Link to={`/notas-venta/${nv.id}`} style={{ color: colors.link, ...sx.mono }}>{nv.numero_lgr}</Link>}
            <span style={{ ...sx.mono, fontWeight: 700, marginLeft: 'auto' }}>$ {fmtMonto(informe.compensacion_importe)}</span>
          </>
        ) : nv ? (
          <>
            <span style={{ fontSize: text.body, fontWeight: 600 }}>NV de compensación</span>
            <Link to={`/notas-venta/${nv.id}`} style={{ color: colors.link, fontWeight: 600, ...sx.mono }}>{nv.numero_lgr}</Link>
            <span style={{ fontSize: text.sec, color: colors.textSec }}>
              {nv.estado === 'borrador' ? 'en borrador — abrila y enviala a Presea. Al facturarse, el evento queda completo.' : `${nv.estado} — esperando la factura de Presea.`}
            </span>
            <Boton variante="send" sm style={{ marginLeft: 'auto' }} onClick={() => navigate(`/notas-venta/${nv.id}`)}>Abrir la NV →</Boton>
          </>
        ) : (
          <>
            <span style={{ fontSize: text.body, color: colors.textSec }}>
              {abierto
                ? <>Cerrá el evento con <b>✓ Cerrar evento</b> (a la derecha): la NV sale del balance congelado.</>
                : <>La NV va al cliente <b>72706 CONSUMOS CASA VG</b> y queda en borrador, lista para enviar a Presea.</>}
            </span>
            <Boton variante="send" disabled={abierto || !comp || generando} onClick={onGenerar} style={{ marginLeft: 'auto' }}
              title={abierto ? 'Primero cerrá el evento' : 'Arma la NV con estos renglones'}>
              {generando ? 'Generando…' : '＋ Generar NV de compensación'}
            </Boton>
          </>
        )}
      </div>

      {comp?.renglones && (
        <div style={{ marginTop: space.lg }}>
          <div style={{ fontSize: text.sec, color: colors.textMeta, marginBottom: space.xs }}>
            {tipo === 'descuento'
              ? `Los vinos superan lo cobrado: descuento parejo del ${fmtMonto(comp.ajuste.porcentaje, 2)} % sobre los vinos.`
              : 'Lo cobrado supera los vinos: la diferencia va como Gastos eventos.'}
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: text.body }}>
            <thead>
              <tr>
                <th style={thNv}>Renglón</th>
                <th style={{ ...thNv, textAlign: 'right', width: 70 }}>Cant.</th>
                <th style={{ ...thNv, textAlign: 'right', width: 120 }}>Precio</th>
                <th style={{ ...thNv, textAlign: 'right', width: 70 }}>Dto.</th>
                <th style={{ ...thNv, textAlign: 'right', width: 136 }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {comp.renglones.map((r, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${colors.borderSoft}` }}>
                  <td style={{ ...celda, lineHeight: 1.3 }}>{ROL_COMP[r.rol](r, vinos, tipo)}</td>
                  <td style={{ ...celda, ...sx.mono, textAlign: 'right' }}>{fmtMonto(r.cantidad, 0)}</td>
                  <td style={{ ...celda, ...sx.mono, textAlign: 'right', whiteSpace: 'nowrap' }}>$ {fmtMonto(r.precio)}</td>
                  <td style={{ ...celda, ...sx.mono, textAlign: 'right', color: colors.textSec }}>{r.descuento ? `${fmtMonto(r.descuento, 2)} %` : ''}</td>
                  <td style={{ ...celda, ...sx.mono, textAlign: 'right', whiteSpace: 'nowrap' }}>$ {fmtMonto(r.total)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={4} style={{ ...celda, textAlign: 'right', fontWeight: 600 }}>Total de la NV</td>
                <td style={{ ...celda, ...sx.mono, textAlign: 'right', fontWeight: 700 }}>$ {fmtMonto(comp.total)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  )
}

function Campo({ label, ayuda, ancho = 1, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, gridColumn: `span ${ancho}`, minWidth: 0 }}>
      <span style={{ fontSize: text.meta, fontWeight: 600, color: colors.textSec }}>{label}</span>
      {children}
      {ayuda && <span style={{ fontSize: text.meta, color: colors.textMeta, lineHeight: 1.35 }}>{ayuda}</span>}
    </label>
  )
}

function Resumen({ label, monto, menos }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', color: colors.textSub, fontSize: text.body }}>
      <span>{label}</span>
      <span style={{ ...sx.mono, whiteSpace: 'nowrap' }}>{menos ? '− ' : ''}$ {fmtMonto(monto || 0)}</span>
    </div>
  )
}

const inp = { ...sx.input, minWidth: 0, width: '100%', boxSizing: 'border-box' }
const grilla = { display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: `${space.lg}px ${space.xl}px`, marginTop: space.lg }
const celda = { padding: '6px 8px', verticalAlign: 'middle' }
const caja = { padding: '10px 14px', borderRadius: radius.md, marginBottom: space.lg, fontSize: text.body }
// Grilla de carga con la misma medida que la de la nota de venta
const tablaNv = { width: '100%', minWidth: 560, borderCollapse: 'collapse', fontSize: text.body, tableLayout: 'fixed' }
const thNv = { ...sx.th, padding: `${space.md}px ${space.md}px` }
const tdNv = { padding: `${space.xs}px ${space.md}px`, verticalAlign: 'middle', fontSize: text.body, color: colors.text }
const celdaNv = { width: '100%', boxSizing: 'border-box', padding: '5px 8px', border: `1px solid ${colors.textPlh}`, borderRadius: radius.xs, fontSize: text.body, textAlign: 'right', ...sx.mono }
const linkBtn = { border: 'none', background: 'none', padding: 0, cursor: 'pointer', color: colors.link, fontSize: text.sec, textDecoration: 'underline' }
